import { parse } from "query-string";
import { all, call, delay, put, takeLatest } from "redux-saga/effects";

import { serializeToQS } from "@/mk2/apps/strollers/containers/StrollersSearch/StrollerSearchForm";
import {
    STROLLERS_SEARCH_LOAD_FACETS,
    STROLLERS_SEARCH_LOAD_HELP,
    STROLLERS_SEARCH_RESULTS_FETCH_TRIGGER,
    STROLLERS_SEARCH_SUBMIT_FORM,
    SearchFacetsCounts,
    StrollersSearchLoadFacetsAction,
    StrollersSearchLoadHelpApiNormalizedResponse,
    StrollersSearchResultsFetchNormalizedResponse,
    StrollersSearchResultsFetchTriggerAction,
    StrollersSearchSubmitFormAction,
    strollersSearchLoadFacetsApi,
    strollersSearchLoadHelpApi,
    strollersSearchResultsFetchApi,
} from "@/mk2/apps/strollers/containers/StrollersSearch/StrollersSearch.actions";
import { STROLLERS_SEARCH_FORM_NAME } from "@/mk2/apps/strollers/containers/StrollersSearch/StrollersSearch.constants";
import { considerStrollersSearchResultUrl } from "@/mk2/apps/strollers/urls";
import { XHRAction, XHRFormError, normalizeError } from "@/mk2/helpers/api";
import { handleXHRFormErrorSaga, handleXHRFormSuccessSaga } from "@/mk2/helpers/form";
import { getLogger } from "@/mk2/logger";
import { HistoryAction } from "@/mk2/services/browserHistory";
import {
    cms_api_v1_get_items_url,
    strollers_api_v2_search_strollers_facets_url,
    strollers_api_v2_search_strollers_url,
} from "@/mk/urls/functions";

const logger = getLogger("strollers/StrollersSearch.sagas");

interface FetchApiResponse {
    body: {
        entities: StrollersSearchResultsFetchNormalizedResponse["entities"];
        result: StrollersSearchResultsFetchNormalizedResponse["result"];
        hasMore: boolean;
        count: number;

        strollersCountPerBrand: Record<number, number>;
        reviewsCountPerBrand: Record<number, number>;
        brandsCount: number;
    };
}

function* fetchStrollersSearchResults({
    q,
    offset,
    xhr,
}: StrollersSearchResultsFetchTriggerAction & XHRAction) {
    yield put(strollersSearchResultsFetchApi.request(q, offset));

    try {
        const response: FetchApiResponse = yield call(() =>
            xhr.get(
                strollers_api_v2_search_strollers_url({
                    ...parse(q),
                    offset,
                    limit: !offset ? 10 : 20,
                }),
            ),
        );

        yield put(
            strollersSearchResultsFetchApi.success(
                q,
                offset,
                response.body.hasMore,
                response.body.count,

                response.body.strollersCountPerBrand,
                response.body.reviewsCountPerBrand,
                response.body.brandsCount,

                {
                    entities: response.body.entities,
                    result: response.body.result,
                },
            ),
        );
    } catch (error) {
        logger.error(error);
        yield put(strollersSearchResultsFetchApi.failure(q, offset, normalizeError(error)));
    }
}

interface LoadFacetsApiResponse {
    body: {
        hits: number;
        facets: SearchFacetsCounts;
    };
}

function* loadFacets({ values, xhr }: StrollersSearchLoadFacetsAction & XHRAction) {
    yield put(strollersSearchLoadFacetsApi.request(values));

    try {
        const response: LoadFacetsApiResponse = yield call(() =>
            xhr.get(strollers_api_v2_search_strollers_facets_url(serializeToQS(values))),
        );

        yield put(
            strollersSearchLoadFacetsApi.success(values, response.body.facets, response.body.hits, {
                // For now we do not return any entities
                entities: {},
                result: {},
            }),
        );
    } catch (error) {
        logger.error(error);
        yield put(strollersSearchLoadFacetsApi.failure(values, normalizeError(error)));
    }
}

interface LoadHelpApiResponse {
    body: {
        entities: StrollersSearchLoadHelpApiNormalizedResponse["entities"];
        result: StrollersSearchLoadHelpApiNormalizedResponse["result"];
    };
}

function* loadHelp({ xhr }: StrollersSearchLoadFacetsAction & XHRAction) {
    yield put(strollersSearchLoadHelpApi.request());

    try {
        const response: LoadHelpApiResponse = yield call(() =>
            xhr.get(
                cms_api_v1_get_items_url({
                    keys: [
                        "type",
                        "type-2kombi",
                        "siblings-type",
                        "price",
                        "color",
                        "brand",
                        "variant-rear-wheel-type",
                        "rotating-fixation-wheels",
                        "number-of-wheels-l",
                        "rear-wheels-diameter",
                        "recommended-age",
                        "maximum-load-per-child-in-kg-range",
                        "availability",
                        "rotating-seat",
                        "swinging",
                        "spacious-carrycot",
                        "spacious-seat",
                        "weight-is-low",
                        "very-good-folding",
                        "big-shopping-basket",
                        "manual-brake-present",
                        "adjustable-handle",
                        "foldable-handrail",
                        "one-hand-folding",
                        "easy-one-hand-manipulation",
                        "foldable-carrycot",
                        "carrycot-has-handle",
                        "back-support-can-be-positioned",
                        "leg-rest-can-be-positioned",
                        "back-support-can-be-in-lying-position",
                        "car-seat-can-be-attached",
                        "supplied-pad",
                        "washable-fabrics",
                        "harness-type-5b",
                        "bumper-bar",
                        "parent-height",
                    ]
                        .map((v) => `stroller-search-form-help-${v}`)
                        .join(","),
                }),
            ),
        );

        yield put(strollersSearchLoadHelpApi.success(response.body));
    } catch (error) {
        logger.error(error);
        yield put(strollersSearchLoadHelpApi.failure(normalizeError(error)));
    }
}

function* submitSearch({
    values,
    history,
}: StrollersSearchSubmitFormAction & XHRAction & HistoryAction) {
    try {
        const data = serializeToQS(values);

        // need a delay to let redux-form dispatch a @@redux-form/START_SUBMIT
        yield delay(1);

        const gotoUrl = considerStrollersSearchResultUrl(data);

        yield handleXHRFormSuccessSaga(STROLLERS_SEARCH_FORM_NAME, history, gotoUrl);
    } catch (error) {
        yield handleXHRFormErrorSaga(STROLLERS_SEARCH_FORM_NAME, error as XHRFormError, logger);
    }
}

export default function* root() {
    yield all([
        takeLatest(STROLLERS_SEARCH_RESULTS_FETCH_TRIGGER, fetchStrollersSearchResults),
        takeLatest(STROLLERS_SEARCH_SUBMIT_FORM, submitSearch),
        takeLatest(STROLLERS_SEARCH_LOAD_FACETS, loadFacets),
        takeLatest(STROLLERS_SEARCH_LOAD_HELP, loadHelp),
    ]);
}
